// ABOUTME: RTK Query endpoints for feedback capture + admin triage, injected into the shared apiSlice (no store edit).
// ABOUTME: Capture: submitFeedback/getMyReports. Admin (/software): list/detail/triage actions + an auth'd screenshot blob fetch.

import { apiSlice } from "../createApiSlice";

// baseUrl is already /api/ — so /v1/feedback resolves to /api/v1/feedback
const API_BASE = "/v1/feedback";
const buildUrl = (endpoint) => API_BASE + endpoint;

// Admin (super-admin / sha) triage endpoints. baseUrl is /api/, so /v1/admin/feedback
// resolves to /api/v1/admin/feedback (lock §6).
const ADMIN_BASE = "/v1/admin/feedback";
const buildAdminUrl = (endpoint) => ADMIN_BASE + endpoint;

// Work-order endpoints (Spec 3). baseUrl is /api/, so /v1/admin/work-orders
// resolves to /api/v1/admin/work-orders (lock §6, super-admin gated server-side).
const ADMIN_WO_BASE = "/v1/admin/work-orders";
const buildWorkOrderUrl = (endpoint) => ADMIN_WO_BASE + endpoint;

// The admin list query string is assembled from the ShDataGrid2 query args. The grid
// calls the endpoint with { args: { page, size, body } } where `body` carries the active
// filters (category / date range / search). We map those to the documented query params.
const buildAdminListUrl = (args = {}) => {
    const params = new URLSearchParams();
    params.set("page", args.page ?? 1);
    params.set("pageSize", args.size ?? 20);

    const body = args.body || {};
    // searchString is the grid's text-search field; map it to `search`.
    if (body.searchString) params.set("search", body.searchString);
    if (body.category) params.set("category", body.category);
    if (body.warehouseId) params.set("warehouseId", body.warehouseId);
    if (body.customerId) params.set("customerId", body.customerId);
    if (body.dateFrom) params.set("dateFrom", body.dateFrom);
    if (body.dateTo) params.set("dateTo", body.dateTo);

    return buildAdminUrl(`?${params.toString()}`);
};

export const feedbackApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        submitFeedback: builder.mutation({
            query: (args) => {
                const { category, narrative, contextJson, screenshot } = args;
                const formData = new FormData();
                formData.append("data", JSON.stringify({ category, narrative, contextJson }));
                if (screenshot) {
                    formData.append("screenshot", screenshot);
                }
                return {
                    url: buildUrl("/"),
                    method: "POST",
                    body: formData,
                };
            },
            invalidatesTags: () => [{ type: "Feedback" }],
        }),
        getMyReports: builder.query({
            query: () => ({
                url: buildUrl("/mine"),
                method: "GET",
            }),
            providesTags: () => [{ type: "Feedback" }],
        }),

        // --- Admin triage endpoints (/software, super-admin gated server-side) ---

        // Paged list for the ShDataGrid2. The backend returns { rows, total, page, pageSize };
        // transformResponse maps that to the { data, totalItems, totalPages } shape the grid reads.
        getAdminFeedback: builder.query({
            query: ({ args }) => ({
                url: buildAdminListUrl(args),
                method: "GET",
            }),
            transformResponse: (response, meta, arg) => {
                const pageSize = arg?.args?.size ?? response?.pageSize ?? 20;
                const total = response?.total ?? 0;
                return {
                    data: response?.rows ?? [],
                    totalItems: total,
                    totalPages: pageSize > 0 ? Math.ceil(total / pageSize) : 0,
                };
            },
            providesTags: () => [{ type: "AdminFeedback" }],
        }),
        getAdminFeedbackDetail: builder.query({
            query: (id) => ({
                url: buildAdminUrl(`/${id}`),
                method: "GET",
            }),
            // Tolerate both the flattened report body and a legacy { success, report } envelope.
            transformResponse: (response) => response?.report ?? response,
            providesTags: () => [{ type: "AdminFeedback" }],
        }),
        getFeedbackDuplicates: builder.query({
            query: (id) => ({
                url: buildAdminUrl(`/${id}/duplicates`),
                method: "GET",
            }),
            providesTags: () => [{ type: "AdminFeedback" }],
        }),
        // Auth'd screenshot bytes (image/png). The endpoint requires the bearer token +
        // super-admin, so a plain <img src> would not authenticate. We fetch via RTK Query
        // (which runs the shared prepareHeaders → Authorization bearer) and convert the
        // response to an object URL in responseHandler; the detail panel sets <img src> to
        // it and revokes it on unmount. Loaded lazily (only when the panel opens a report
        // that has a screenshotPath).
        getFeedbackScreenshot: builder.mutation({
            query: (id) => ({
                url: buildAdminUrl(`/${id}/screenshot`),
                method: "GET",
                responseHandler: async (response) => {
                    if (!response.ok) {
                        throw new Error("Network response was not ok");
                    }
                    const blob = new Blob([await response.blob()], { type: "image/png" });
                    return { data: URL.createObjectURL(blob) };
                },
            }),
        }),
        analyzeFeedback: builder.mutation({
            query: (id) => ({
                url: buildAdminUrl(`/${id}/analyze`),
                method: "POST",
            }),
            invalidatesTags: () => [{ type: "AdminFeedback" }],
        }),
        addFeedbackContext: builder.mutation({
            query: ({ id, adminContext }) => ({
                url: buildAdminUrl(`/${id}/context`),
                method: "POST",
                body: { adminContext },
            }),
            invalidatesTags: () => [{ type: "AdminFeedback" }],
        }),
        approveFeedback: builder.mutation({
            query: ({ id, overrides }) => ({
                url: buildAdminUrl(`/${id}/approve`),
                method: "POST",
                body: { overrides },
            }),
            invalidatesTags: () => [{ type: "AdminFeedback" }, { type: "WorkOrder" }],
        }),
        rejectFeedback: builder.mutation({
            query: ({ id, reason }) => ({
                url: buildAdminUrl(`/${id}/reject`),
                method: "POST",
                body: { reason },
            }),
            invalidatesTags: () => [{ type: "AdminFeedback" }],
        }),
        linkFeedbackDuplicate: builder.mutation({
            query: ({ id, duplicateOfReportId }) => ({
                url: buildAdminUrl(`/${id}/link-duplicate`),
                method: "POST",
                body: { duplicateOfReportId },
            }),
            invalidatesTags: () => [{ type: "AdminFeedback" }],
        }),
        unlinkFeedbackDuplicate: builder.mutation({
            query: (id) => ({
                url: buildAdminUrl(`/${id}/unlink-duplicate`),
                method: "POST",
            }),
            invalidatesTags: () => [{ type: "AdminFeedback" }],
        }),
        markFeedbackResolved: builder.mutation({
            query: (id) => ({
                url: buildAdminUrl(`/${id}/mark-resolved`),
                method: "POST",
            }),
            invalidatesTags: () => [{ type: "AdminFeedback" }],
        }),
        notifyFeedbackTested: builder.mutation({
            query: (id) => ({
                url: buildAdminUrl(`/${id}/notify-tested`),
                method: "POST",
            }),
            invalidatesTags: () => [{ type: "AdminFeedback" }],
        }),

        // --- Work-order endpoints (Spec 3, /software, super-admin gated server-side) ---

        // Pending agent queue. The backend returns a bare array; ShDataGrid2 reads the
        // { data, totalItems, totalPages } shape, so transformResponse maps the array to it
        // (single page — the queue is small). Invoked by the grid with { args: { ... } }.
        getPendingWorkOrders: builder.query({
            query: () => ({
                url: buildWorkOrderUrl("/pending"),
                method: "GET",
            }),
            transformResponse: (response) => {
                const arr = Array.isArray(response) ? response : (Array.isArray(response?.data) ? response.data : []);
                return {
                    data: arr,
                    totalItems: arr.length,
                    totalPages: 1,
                };
            },
            providesTags: () => [{ type: "WorkOrder" }],
        }),
        getWorkOrder: builder.query({
            query: (id) => ({
                url: buildWorkOrderUrl(`/${id}`),
                method: "GET",
            }),
            providesTags: () => [{ type: "WorkOrder" }],
        }),
        getWorkOrderByReport: builder.query({
            query: (feedbackReportId) => ({
                url: buildWorkOrderUrl(`/by-report/${feedbackReportId}`),
                method: "GET",
            }),
            providesTags: () => [{ type: "WorkOrder" }],
        }),
        claimWorkOrder: builder.mutation({
            query: (id) => ({
                url: buildWorkOrderUrl(`/${id}/claim`),
                method: "POST",
            }),
            invalidatesTags: () => [{ type: "WorkOrder" }, { type: "AdminFeedback" }],
        }),
        planWorkOrder: builder.mutation({
            query: ({ id, planReport }) => ({
                url: buildWorkOrderUrl(`/${id}/plan`),
                method: "POST",
                body: { planReport },
            }),
            invalidatesTags: () => [{ type: "WorkOrder" }, { type: "AdminFeedback" }],
        }),
        acceptWorkOrderPlan: builder.mutation({
            query: (id) => ({
                url: buildWorkOrderUrl(`/${id}/accept-plan`),
                method: "POST",
            }),
            invalidatesTags: () => [{ type: "WorkOrder" }, { type: "AdminFeedback" }],
        }),
        resultWorkOrder: builder.mutation({
            query: ({ id, resultReport, branchName, prLink }) => ({
                url: buildWorkOrderUrl(`/${id}/result`),
                method: "POST",
                body: { resultReport, branchName, prLink },
            }),
            invalidatesTags: () => [{ type: "WorkOrder" }, { type: "AdminFeedback" }],
        }),
        qualityGateWorkOrder: builder.mutation({
            query: ({ id, status, reportJson }) => ({
                url: buildWorkOrderUrl(`/${id}/quality-gate`),
                method: "POST",
                body: { status, reportJson },
            }),
            invalidatesTags: () => [{ type: "WorkOrder" }, { type: "AdminFeedback" }],
        }),
        approveWorkOrderMerge: builder.mutation({
            query: (id) => ({
                url: buildWorkOrderUrl(`/${id}/approve-merge`),
                method: "POST",
            }),
            invalidatesTags: () => [{ type: "WorkOrder" }, { type: "AdminFeedback" }],
        }),
        haltWorkOrder: builder.mutation({
            query: ({ id, reason }) => ({
                url: buildWorkOrderUrl(`/${id}/halt`),
                method: "POST",
                body: { reason },
            }),
            invalidatesTags: () => [{ type: "WorkOrder" }, { type: "AdminFeedback" }],
        }),
    }),
});

export const {
    useSubmitFeedbackMutation,
    useGetMyReportsQuery,
    useGetAdminFeedbackQuery,
    useGetAdminFeedbackDetailQuery,
    useGetFeedbackDuplicatesQuery,
    useGetFeedbackScreenshotMutation,
    useAnalyzeFeedbackMutation,
    useAddFeedbackContextMutation,
    useApproveFeedbackMutation,
    useRejectFeedbackMutation,
    useLinkFeedbackDuplicateMutation,
    useUnlinkFeedbackDuplicateMutation,
    useMarkFeedbackResolvedMutation,
    useNotifyFeedbackTestedMutation,
    useGetPendingWorkOrdersQuery,
    useGetWorkOrderQuery,
    useGetWorkOrderByReportQuery,
    useClaimWorkOrderMutation,
    usePlanWorkOrderMutation,
    useAcceptWorkOrderPlanMutation,
    useResultWorkOrderMutation,
    useQualityGateWorkOrderMutation,
    useApproveWorkOrderMergeMutation,
    useHaltWorkOrderMutation,
} = feedbackApiSlice;
